/**
 * The composer — a new thread, a reply, or an edit. `docs/plans/community.md`
 * mock C.
 *
 * **Attachments come from what is already open**: the active chart (pictured
 * now, and copied), the layout, the screen on the screener, a plan from the
 * journal, a track list. There is no upload of your own images — only charts
 * the app captures (owner, 2026-09-29: own uploads need content scanning).
 *
 * The picture is taken in the browser by `captureImage()` and shrunk to fit
 * 2000 × 1200 *before* it is sent: a hi-DPI screen captures at 2–3× its CSS
 * size, and refusing the chart of everyone with a good monitor would be absurd.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ChartGroup, Layout, Plan } from "@qufolio/model";

import {
  createPost,
  editPost,
  imageSrc,
  uploadSnapshot,
  type CommunityRole,
  type Embed,
  type Post,
  type Topic,
} from "../../api/community";
import { cachedCatalog } from "../../api/indicators";
import { fetchPlansFor } from "../../api/journal";
import { chartImage } from "../../lib/chart-snapshot";
import { canStartThread, chartEmbed, embedLabel, gatedCategories, layoutEmbed } from "../../lib/community";
import { trackListsSnapshot } from "../../lib/track-lists";
import { Btn, CARD, ErrorLine, META, PostText } from "./CommunityParts";
import { FormatBar } from "./FormatBar";

export interface AttachSources {
  /** The active chart. */
  chart?: ChartGroup;
  /** The layout on screen. */
  layout?: Layout;
  /** The screen on the screener, as a copy. */
  screen?: { name: string; config: Record<string, unknown> };
}

const MAX_W = 2000;
const MAX_H = 1200;
const MAX_EMBEDS = 4;

/** A data URL shrunk to fit the server's limits, as bare base64. */
async function fitPng(dataUrl: string): Promise<string> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("the chart picture could not be read"));
    img.src = dataUrl;
  });
  const scale = Math.min(1, MAX_W / img.naturalWidth, MAX_H / img.naturalHeight);
  if (scale >= 1) return dataUrl.split(",", 2)[1] ?? "";
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.floor(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("the chart picture could not be resized");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png").split(",", 2)[1] ?? "";
}

async function pictureOf(orgKey: string, group: ChartGroup): Promise<{ snapshotId?: string; imageUrl?: string }> {
  const dataUrl = await chartImage(group.id);
  if (!dataUrl) return {};
  const made = await uploadSnapshot(orgKey, await fitPng(dataUrl), JSON.parse(JSON.stringify(group)));
  return { snapshotId: made.snapshotId, imageUrl: made.imageUrl };
}

/** `ema_21` → the catalog's category for `ema`. */
function categoryOf(alias: string): string | undefined {
  const head = alias.split("_", 1)[0]!.toLowerCase();
  for (const entry of cachedCatalog()) {
    if (entry.outputs.some((o) => o.name.toLowerCase() === head)) return entry.category;
  }
  return undefined;
}

export function CommunityComposer({
  orgKey,
  role,
  topics,
  initialTopicId,
  parentId,
  edit,
  attach,
  autoAttachChart,
  onDone,
  onCancel,
}: {
  orgKey: string;
  role: CommunityRole;
  topics: Topic[];
  initialTopicId?: string | null;
  parentId?: string;
  edit?: Post;
  attach: AttachSources;
  autoAttachChart?: boolean;
  onDone: (post: Post) => void;
  onCancel: () => void;
}) {
  const reply = !!parentId || (edit ? edit.depth > 0 : false);
  const postable = useMemo(() => topics.filter((t) => canStartThread(role, t)), [topics, role]);
  const [topicId, setTopicId] = useState<string>(
    edit?.topicId ?? (initialTopicId && postable.some((t) => t.id === initialTopicId) ? initialTopicId : postable[0]?.id ?? ""),
  );
  const [title, setTitle] = useState(edit?.title ?? "");
  const [body, setBody] = useState(edit?.body ?? "");
  const [embeds, setEmbeds] = useState<Embed[]>(edit?.embeds ?? []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState<null | "plan" | "list">(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const topic = topics.find((t) => t.id === (edit?.topicId ?? topicId));
  const full = embeds.length >= MAX_EMBEDS;

  const addChart = async () => {
    if (!attach.chart) return;
    setBusy("Taking the picture…");
    setError(null);
    try {
      const pic = await pictureOf(orgKey, attach.chart);
      setEmbeds((was) => [...was, { ...chartEmbed(attach.chart!, pic.snapshotId), ...(pic.imageUrl ? { imageUrl: pic.imageUrl } : {}) }]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the chart could not be attached");
    } finally {
      setBusy(null);
    }
  };

  // "Share to group" opens the composer with the chart already on it.
  useEffect(() => {
    if (autoAttachChart && attach.chart && !edit) void addChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLayout = async () => {
    if (!attach.layout) return;
    setBusy("Taking the pictures…");
    setError(null);
    try {
      const charts = attach.layout.sections
        .filter((s) => s.kind === "chartGroup" && s.config)
        .map((s) => s.config as ChartGroup)
        .slice(0, 4);
      const pics = await Promise.all(charts.map((g) => pictureOf(orgKey, g).catch(() => ({}) as { snapshotId?: string })));
      const ids = pics.map((p) => p.snapshotId).filter((id): id is string => !!id);
      setEmbeds((was) => [...was, layoutEmbed(attach.layout!, ids)]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the layout could not be attached");
    } finally {
      setBusy(null);
    }
  };

  const addScreen = () => {
    if (!attach.screen) return;
    setEmbeds((was) => [...was, { kind: "screen", name: attach.screen!.name, config: attach.screen!.config }]);
  };

  const openPlans = async () => {
    if (!attach.chart) return;
    setPicker("plan");
    try {
      setPlans(await fetchPlansFor(attach.chart.symbol));
    } catch {
      setPlans([]);
    }
  };

  const chartAliases = embeds.flatMap((e) =>
    e.kind === "chart" ? (e.indicators ?? []) : [],
  );
  const gated = topic?.isOpen && embeds.some((e) => (e.kind === "chart" && e.snapshotId) || (e.kind === "layout" && e.snapshotIds?.length))
    ? gatedCategories(chartAliases, categoryOf)
    : [];
  const needsConfirm = gated.length > 0 && !confirmed;

  const submit = async () => {
    setError(null);
    if (!reply && !edit && !topicId) return setError("pick a topic");
    if (!reply && !title.trim()) return setError("a thread needs a title");
    if (!body.trim() && embeds.length === 0) return setError("write something first");
    setBusy("Posting…");
    try {
      const done = edit
        ? await editPost(orgKey, edit.id, { title, body, embeds })
        : await createPost(orgKey, reply ? { parentId, body, embeds } : { topicId, title, body, embeds });
      onDone(done);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "that did not post");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ ...CARD, padding: 10 }}>
      {!reply && !edit && (
        <label style={LABEL}>
          Topic
          <select value={topicId} onChange={(e) => setTopicId(e.target.value)} style={INPUT}>
            {postable.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.isOpen ? " (Open House)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {!reply && (
        <label style={LABEL}>
          Title
          <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} style={INPUT} placeholder="$NVDA tight flag above the 21" />
        </label>
      )}
      <div style={LABEL}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{reply ? "Reply" : "Body"}</span>
          <span style={{ flex: 1 }} />
          {/* Write / Preview: Preview is drawn by the same PostText readers see. */}
          <button type="button" onClick={() => setPreview(false)} style={tab(!preview)}>Write</button>
          <button type="button" onClick={() => setPreview(true)} style={tab(preview)}>Preview</button>
        </div>
        {preview ? (
          <div style={{ ...INPUT, minHeight: reply ? 60 : 110, color: "var(--qf-text)" }}>
            {body.trim() ? <PostText text={body} onSymbol={() => {}} /> : <span style={META}>Nothing to preview yet.</span>}
          </div>
        ) : (
          <>
            <FormatBar textRef={bodyRef} value={body} onChange={setBody} />
            <textarea
              ref={bodyRef}
              aria-label={reply ? "Reply" : "Body"}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={reply ? 3 : 8}
              style={{ ...INPUT, marginTop: 0, resize: "vertical", fontFamily: "inherit" }}
              placeholder="Type $ and a symbol to link it. Select text and use the bar above to format it."
            />
          </>
        )}
      </div>

      {embeds.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, margin: "4px 0" }}>
          {embeds.map((e, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, ...META }}>
              {e.kind === "chart" && e.imageUrl && (
                <img src={imageSrc(e.imageUrl)} alt="" style={{ width: 64, height: 36, objectFit: "cover", borderRadius: 3 }} />
              )}
              <span style={{ flex: 1 }}>{embedLabel(e)}{e.kind === "chart" && !e.snapshotId ? " (no picture)" : ""}</span>
              <Btn small onClick={() => setEmbeds((was) => was.filter((_, j) => j !== i))} title="Remove">✕</Btn>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
        <Btn small disabled={!attach.chart || full || !!busy} onClick={() => void addChart()} title="The active chart: a picture of it now, and a copy readers can open live">📷 Active chart</Btn>
        <Btn small disabled={!attach.layout || full || !!busy} onClick={() => void addLayout()}>▦ Layout</Btn>
        <Btn small disabled={!attach.screen || full} onClick={addScreen}>▦ Screen</Btn>
        <Btn small disabled={!attach.chart || full} onClick={() => void openPlans()}>🎯 Plan…</Btn>
        <Btn small disabled={full} onClick={() => setPicker(picker === "list" ? null : "list")}>★ List…</Btn>
      </div>

      {picker === "plan" && (
        <div style={CARD}>
          {plans.length === 0 ? (
            <div style={META}>No plans for {attach.chart?.symbol} in your journal.</div>
          ) : (
            plans.map((p) => (
              <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", ...META }}>
                <span style={{ flex: 1 }}>{p.symbol} {p.direction} · {p.entry} / {p.stop}</span>
                <Btn
                  small
                  onClick={() => {
                    setEmbeds((was) => [
                      ...was,
                      {
                        kind: "plan",
                        symbol: p.symbol,
                        direction: p.direction,
                        entry: p.entry,
                        stop: p.stop,
                        targets: (p.targets ?? []).map((t) => t.price),
                        entryOrder: p.entryOrder,
                        frozenAt: new Date().toISOString(),
                      },
                    ]);
                    setPicker(null);
                  }}
                >
                  Attach
                </Btn>
              </div>
            ))
          )}
        </div>
      )}
      {picker === "list" && (
        <div style={CARD}>
          {trackListsSnapshot().length === 0 ? (
            <div style={META}>You have no lists yet.</div>
          ) : (
            trackListsSnapshot().map((l) => (
              <div key={l.id} style={{ display: "flex", gap: 8, alignItems: "center", ...META }}>
                <span style={{ flex: 1 }}>{l.name} ({l.memberCount})</span>
                <Btn
                  small
                  onClick={() => {
                    setEmbeds((was) => [...was, { kind: "tracklist", name: l.name, symbols: l.members.map((m) => m.symbol) }]);
                    setPicker(null);
                  }}
                >
                  Attach
                </Btn>
              </div>
            ))
          )}
        </div>
      )}

      {gated.length > 0 && (
        <div style={{ ...CARD, borderColor: "var(--qf-warn)", fontSize: 12.5 }}>
          <span style={{ color: "var(--qf-warn)" }}>⚠</span> This is the <b>Open House</b>: the picture shows{" "}
          <b>{gated.join(", ")}</b> indicators to people outside the group.{" "}
          <label style={{ whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> Post anyway
          </label>
        </div>
      )}
      {role === "visitor" && (
        <div style={META}>Your post is checked by the group's hosts before anyone else sees it.</div>
      )}
      {busy && <div style={META}>{busy}</div>}
      <ErrorLine error={error} />
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn primary disabled={!!busy || needsConfirm} onClick={() => void submit()}>
          {edit ? "Save" : reply ? "Reply" : "Post"}
        </Btn>
      </div>
    </div>
  );
}

const LABEL: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--qf-text-secondary)", marginBottom: 6 };
const INPUT: React.CSSProperties = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  marginTop: 2,
  padding: "5px 7px",
  fontSize: 13,
  color: "var(--qf-text)",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
};

const tab = (on: boolean): React.CSSProperties => ({
  border: 0,
  borderBottom: `2px solid ${on ? "var(--qf-accent)" : "transparent"}`,
  background: "none",
  color: on ? "var(--qf-text)" : "var(--qf-text-secondary)",
  fontSize: 11.5,
  padding: "1px 4px",
  cursor: "pointer",
});
